import React, { useEffect } from 'react'

type AppViewportMode = 'desktop' | 'compact' | 'tablet' | 'mobile'

const getViewportMetrics = () => {
  const visualViewport = window.visualViewport
  const scale = visualViewport?.scale ?? 1

  // Prefer window.innerWidth, visualViewport.width shrinks with pinch-zoom,
  // which would otherwise push zoomed-in users into tablet/mobile modes even
  // on big screens. innerWidth reflects the CSS layout width.
  const width = Math.round(window.innerWidth)
  const height = Math.round(window.innerHeight)

  // innerWidth already describes layout CSS pixels. Multiplying by pinch scale
  // incorrectly switches a zoomed phone to a desktop layout.
  const effectiveWidth = width
  const effectiveHeight = height

  return { width, height, scale, effectiveWidth, effectiveHeight }
}

export const getViewportMode = ({
  effectiveWidth,
  effectiveHeight,
}: {
  effectiveWidth: number
  effectiveHeight: number
}): AppViewportMode => {
  // Height changes (including the software keyboard) must not switch layouts.
  if (effectiveWidth <= 768) return 'mobile'
  if (effectiveWidth <= 1024) return 'tablet'
  if (effectiveWidth <= 1440) return 'compact'
  return 'desktop'
}

interface AppViewportManagerProps {
  children: React.ReactNode
}

export const AppViewportManager: React.FC<AppViewportManagerProps> = ({ children }) => {
  useEffect(() => {
    const root = document.documentElement
    let animationFrameId = 0

    const syncViewport = () => {
      animationFrameId = 0

      const metrics = getViewportMetrics()
      const viewportMode = getViewportMode(metrics)

      root.dataset.appViewport = viewportMode
      root.style.setProperty('--app-viewport-width', `${metrics.width}px`)
      root.style.setProperty('--app-viewport-height', `${metrics.height}px`)
      root.style.setProperty('--app-viewport-scale', String(metrics.scale))
      root.style.setProperty('--app-viewport-effective-width', `${metrics.effectiveWidth}px`)
    }

    const scheduleSync = () => {
      if (animationFrameId !== 0) return
      animationFrameId = window.requestAnimationFrame(syncViewport)
    }

    scheduleSync()

    window.addEventListener('resize', scheduleSync)
    window.addEventListener('orientationchange', scheduleSync)
    window.visualViewport?.addEventListener('resize', scheduleSync)
    window.visualViewport?.addEventListener('scroll', scheduleSync)

    return () => {
      if (animationFrameId !== 0) {
        window.cancelAnimationFrame(animationFrameId)
      }

      window.removeEventListener('resize', scheduleSync)
      window.removeEventListener('orientationchange', scheduleSync)
      window.visualViewport?.removeEventListener('resize', scheduleSync)
      window.visualViewport?.removeEventListener('scroll', scheduleSync)
    }
  }, [])

  return <>{children}</>
}
